import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { loadModules } from 'esri-loader';
import * as fromServices from '../../services';
import { MapMarkersTypes } from './map.model';

@Component({
  selector: 'hdw-map-container',
  templateUrl: './map-container.component.html',
  styleUrls: ['./map-container.component.scss']
})
export class MapContainerComponent implements OnInit, OnChanges {
  @ViewChild('mapViewDiv', { static: true }) private mapViewEl: ElementRef;
  @Input() public setFocus: { lat: number; lon: number; zoom: number; id: number };
  @Input() locationData: {
    locations: fromServices.LocationItem[];
    selectedLocation: { lat: string; lon: string; locationName: string };
    radius: number;
  };
  @Output() public selectedMarkerEmitter: EventEmitter<number>;
  public canShowLoader: boolean;
  private mapMarkerTypes = MapMarkersTypes;
  private graphicsLayer: any;
  private locationType: fromServices.LocatorTools;
  private _mapView: any;

  constructor(
    private toolIdentificationService: fromServices.ToolIdentifierService,
    private locationStateService: fromServices.LocationStateService
  ) {
    this.selectedMarkerEmitter = new EventEmitter<number>();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['locationData']?.currentValue) {
      const element: HTMLElement = document.getElementById('hdw-map');
      const rect = element.getBoundingClientRect();
      // Added map to support mobile adaptive view if condition supports mobile
      // else condition supports desktop
      if (rect.top > 0) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
        scrollTo(0, window.innerHeight);
      }

      this.loadMap();
    }
    if (changes['setFocus']?.currentValue) {
      this.handleSetFocus();
    }
  }

  public ngOnInit(): void {
    this.locationType = this.toolIdentificationService.getCurrentTool();
    this.loadMap();
  }

  async loadMap() {
    this.canShowLoader = true;
    try {
      const [Map, MapView, Graphic,Basemap, GraphicsLayer,VectorTileLayer, BasemapGallery,
        LocalBasemapsSource,
        PortalBasemapsSource,
        ScaleBar, PictureMarkerSymbol] = await loadModules([
        'esri/Map',
        'esri/views/MapView',
        'esri/Graphic',
        "esri/Basemap",
        'esri/layers/GraphicsLayer',
        "esri/layers/VectorTileLayer",
        "esri/widgets/BasemapGallery",
    "esri/widgets/BasemapGallery/support/LocalBasemapsSource",
    "esri/widgets/BasemapGallery/support/PortalBasemapsSource",
        'esri/widgets/ScaleBar',
        'esri/symbols/PictureMarkerSymbol',
        'esri/geometry/SpatialReference',
        'esri/geometry/Extent',
        'esri/geometry/Point',
        'esri/widgets/Zoom',
        'esri/widgets/Search',
        'esri/geometry/geometryEngine'
      ]);

      const mapBaseLayer = new VectorTileLayer({
        // Topographic style with US worldview
        url: "https://www.arcgis.com/sharing/rest/content/items/6ee2da1b8303471c9e3340d1277926bc/resources/styles/root.json"
    });

    const basemap = new Basemap({
      // set the VTL as the basemap layer
      baseLayers: [mapBaseLayer]
  });

      var map = new Map({
          basemap
          //"streets"
      });

      map.removeAll();
      // Create the map view

      let center = [-98.623516, 40.716717];
      let zoom = 4;
      if (this.locationData?.selectedLocation) {
        center = [
          parseFloat(this.locationData.selectedLocation.lon),
          parseFloat(this.locationData.selectedLocation.lat)
        ];
        zoom = this.getZoom(this.locationData.radius);
      }
      this._mapView = new MapView({
        container: this.mapViewEl.nativeElement,
        map: map,
        center: center,
        zoom: zoom
      });

      // If we have a selected location we will set load it into our locationStateService
      if (this.locationData?.selectedLocation) {
        this.locationStateService.Radius = this.locationData.radius;
        this.locationStateService.Latitude = parseFloat(this.locationData.selectedLocation.lat);
        this.locationStateService.Longitude = parseFloat(this.locationData.selectedLocation.lon);
        this.locationStateService.Map = this._mapView;
        this.locationStateService.Zoom = zoom;
      } else {
        this.locationStateService.Radius = null;
        this.locationStateService.Latitude = null;
        this.locationStateService.Longitude = null;
        this.locationStateService.Map = null;
        this.locationStateService.Zoom = null;
      }

      // Add the Zoom widget to the bottom-right corner of the map view
      this._mapView.ui.move('zoom', 'bottom-right');

      // Create the Scalebar widget
      const scaleBar = new ScaleBar({
        view: this._mapView,
        unit: 'dual',
        style: 'ruler'
      });

      this._mapView.ui.add(scaleBar, 'bottom-left');
      // Create a graphics layer
      this.graphicsLayer = new GraphicsLayer({
        id: 'glSearchLocations'
      });
      map.add(this.graphicsLayer);

      if (this.locationData?.selectedLocation) {
        const markerProperties =
          this.locationType === fromServices.LocatorTools.HealthCenter
            ? this.mapMarkerTypes.HealthCenterSearch
            : this.mapMarkerTypes.GeneralSearch;
        const selectedLocationMarker = new PictureMarkerSymbol(markerProperties);
        const point = {
          type: 'point',
          longitude: this.locationData.selectedLocation.lon,
          latitude: this.locationData.selectedLocation.lat
        };

        const graphic = new Graphic({
          geometry: point,
          symbol: selectedLocationMarker,
          attributes: {
            name: this.locationData.selectedLocation.locationName,
            id: 'selectedLocation'
          }
        });
        this.graphicsLayer.add(graphic);
      }

      if (this.locationData?.locations && this.locationData?.locations.length) {
        const pictureMarkerSymbol = new PictureMarkerSymbol(this.mapMarkerTypes.General);
        this.locationData.locations.forEach(location => {
          // Add markers to the map
          const point = {
            type: 'point',
            longitude: location.Longitude,
            latitude: location.Latitude
          };

          const graphic = new Graphic({
            geometry: point,
            symbol: pictureMarkerSymbol,
            attributes: {
              name: location.CtrNm,
              id: location.Id,
              isActive: false
            }
          });
          this.graphicsLayer.add(graphic);
        });
      }

      this._mapView.on('click', event => {
        this._mapView.hitTest(event).then(response => {
          if (response.results.length) {
            const id = response.results[0].graphic?.attributes?.id;
            if (id && id !== 'selectedLocation') {
              // First we have to check if we have any other Active Layer and switch it out
              // for a general marker
              this.graphicsLayer.graphics.items.forEach((item, index) => {
                if (item.attributes.isActive) {
                  const generalPictureMarkerSymbol = new PictureMarkerSymbol(this.mapMarkerTypes.General);

                  const newGeneralPoint = {
                    type: 'point',
                    longitude: this.graphicsLayer.graphics.items[index].geometry.longitude,
                    latitude: this.graphicsLayer.graphics.items[index].geometry.latitude
                  };

                  const newGeneralGraphic = new Graphic({
                    geometry: newGeneralPoint,
                    symbol: generalPictureMarkerSymbol,
                    attributes: {
                      name: this.graphicsLayer.graphics.items[index].attributes.name,
                      id: this.graphicsLayer.graphics.items[index].attributes.id,
                      isActive: false
                    }
                  });
                  this.graphicsLayer.remove(this.graphicsLayer.graphics.items[index]);
                  this.graphicsLayer.add(newGeneralGraphic);
                }
              });

              // Create the new Marker with the Active Image
              const newPictureMarkerSymbol = new PictureMarkerSymbol(this.mapMarkerTypes.Selected);
              const newPoint = {
                type: 'point',
                longitude: response.results[0].graphic.geometry.longitude,
                latitude: response.results[0].graphic.geometry.latitude
              };

              const newGraphic = new Graphic({
                geometry: newPoint,
                symbol: newPictureMarkerSymbol,
                attributes: {
                  name: response.results[0].graphic.attributes.name,
                  id: id,
                  isActive: true
                }
              });
              this.graphicsLayer.remove(response.results[0].graphic);
              this.graphicsLayer.add(newGraphic);
              this.selectedMarkerEmitter.emit(id);
              this._mapView.goTo({
                center: [response.results[0].graphic.geometry.longitude, response.results[0].graphic.geometry.latitude],
                zoom: 14
              });
              this.locationStateService.Zoom = 14;
              this.locationStateService.Latitude = response.results[0].graphic.geometry.latitude;
              this.locationStateService.Longitude = response.results[0].graphic.geometry.longitude;
            } else if (id === 'selectedLocation') {
              this._mapView.goTo({
                center: [response.results[0].graphic.geometry.longitude, response.results[0].graphic.geometry.latitude],
                zoom: 14
              });
              this.locationStateService.Zoom = 14;
              this.locationStateService.Latitude = response.results[0].graphic.geometry.latitude;
              this.locationStateService.Longitude = response.results[0].graphic.geometry.longitude;
            }
          }
        });
      });

      this.canShowLoader = false;

      return this._mapView;
    } catch (error) {
      this.canShowLoader = false;
      console.error('Esri Loader Error:', error);
    }
  }

  private getZoom(radius: number): number {
    let zoom = 4;
    if (radius) {
      if (radius <= 30) {
        zoom = 11;
      } else if (radius < 100) {
        zoom = 9;
      } else if (radius >= 100 && radius < 200) {
        zoom = 7;
      } else if (radius >= 200 && radius < 300) {
        zoom = 6;
      } else {
        zoom = 4;
      }
    }
    return zoom;
  }

  async handleSetFocus() {
    try {
      const [Graphic, PictureMarkerSymbol] = await loadModules(['esri/Graphic', 'esri/symbols/PictureMarkerSymbol']);
      this._mapView.goTo({
        center: [this.setFocus.lon, this.setFocus.lat],
        zoom: this.setFocus.zoom
      });
      this.locationStateService.Longitude = this.setFocus.lon;
      this.locationStateService.Latitude = this.setFocus.lat;
      this.locationStateService.Zoom = this.setFocus.zoom;
      this.graphicsLayer.graphics.items.forEach((item, index) => {
        if (item.attributes.isActive) {
          const generalPictureMarkerSymbol = new PictureMarkerSymbol(this.mapMarkerTypes.General);

          const newGeneralPoint = {
            type: 'point',
            longitude: this.graphicsLayer.graphics.items[index].geometry.longitude,
            latitude: this.graphicsLayer.graphics.items[index].geometry.latitude
          };

          const newGeneralGraphic = new Graphic({
            geometry: newGeneralPoint,
            symbol: generalPictureMarkerSymbol,
            attributes: {
              name: this.graphicsLayer.graphics.items[index].attributes.name,
              id: this.graphicsLayer.graphics.items[index].attributes.id,
              isActive: false
            }
          });
          this.graphicsLayer.remove(this.graphicsLayer.graphics.items[index]);
          this.graphicsLayer.add(newGeneralGraphic);
        }

        if (this.graphicsLayer.graphics.items[index].attributes.id === this.setFocus.id) {
          const newPictureMarkerSymbol = new PictureMarkerSymbol(this.mapMarkerTypes.Selected);
          const newPoint = {
            type: 'point',
            longitude: this.graphicsLayer.graphics.items[index].geometry.longitude,
            latitude: this.graphicsLayer.graphics.items[index].geometry.latitude
          };
          const newGraphic = new Graphic({
            geometry: newPoint,
            symbol: newPictureMarkerSymbol,
            attributes: {
              name: this.graphicsLayer.graphics.items[index].attributes.name,
              id: this.setFocus.id,
              isActive: true
            }
          });
          this.graphicsLayer.remove(this.graphicsLayer.graphics.items[index]);
          this.graphicsLayer.add(newGraphic);
        }
      });
    } catch (error) {
      console.error('Esri Loader Error:', error);
    }
  }
}
